import { useState, FormEvent } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import {
  Button,
  Input,
  Label,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
  useTheme,
} from '@lognai/synapse-design-system-v2';
import { Logo } from '../../components/Logo';
import { useAuth } from '../../hooks/useAuth';
import { usePasswordPolicy } from '../../hooks/usePasswordPolicy';
import { PasswordRequirements } from '@lognai/plugin-components';
import { firstPasswordProblem } from '@lognai/plugin-sdk';

export default function ResetPasswordPage() {
  const { theme } = useTheme();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { resetPassword, isResetPasswordPending } = useAuth();
  const token = searchParams.get('token') ?? '';
  // An admin-generated first-password link (`&welcome=1`): same flow, the
  // copy says "set" rather than "reset" since there is nothing to reset yet.
  const isWelcome = searchParams.get('welcome') === '1';

  const policy = usePasswordPolicy();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!token) {
    return (
      <Card className="w-full max-w-md">
        <CardHeader className="flex flex-col items-center space-y-4 text-center pb-6">
          <Logo variant={theme === 'light' ? 'dark' : 'white'} className="h-12 w-auto" />
          <CardTitle className="text-xl font-normal">Invalid Reset Link</CardTitle>
          <CardDescription>
            This link is missing a reset token. Please request a new one.
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center">
          <Link to="/forgot-password">
            <Button className="w-full">Request New Reset Link</Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    // The full policy the server enforces, not just its length rule — this
    // form checked length alone and left the rest to be discovered from the
    // rejection, one requirement per round trip (QA ledger AUTH-008).
    const problem = firstPasswordProblem(policy, newPassword);
    if (problem) {
      setError(problem);
      return;
    }

    const result = await resetPassword({ token, newPassword });

    if (result.success) {
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } else {
      setError(result.error || 'Failed to reset password. The link may have expired.');
    }
  };

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="flex flex-col items-center space-y-4 text-center pb-6">
        <Logo variant={theme === 'light' ? 'dark' : 'white'} className="h-12 w-auto" />
        <CardTitle className="text-xl font-normal">
          {isWelcome ? 'Set your password' : 'Reset your password'}
        </CardTitle>
        {!success && (
          <CardDescription>
            {isWelcome
              ? 'Choose a password to finish setting up your account.'
              : 'Choose a new password for your account.'}
          </CardDescription>
        )}
      </CardHeader>

      <CardContent>
        {success ? (
          <div className="space-y-4 text-center">
            <div className="rounded-lg bg-green-50 p-4 text-sm text-green-700 dark:bg-green-900/20 dark:text-green-400">
              {isWelcome
                ? 'Your password is set — you can now sign in.'
                : 'Your password has been reset successfully! Redirecting you to Sign In...'}
            </div>
            <Link
              to="/login"
              className="block text-sm text-primary underline-offset-4 hover:underline"
            >
              Go to Sign In now
            </Link>
          </div>
        ) : (
          <form method="post" onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Choose a new password"
                required
                autoFocus
              />
              <PasswordRequirements policy={policy} password={newPassword} className="pt-1" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm Password</Label>
              <Input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter your new password"
                required
              />
            </div>

            {error && (
              <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </div>
            )}

            <Button type="submit" className="w-full" disabled={isResetPasswordPending}>
              {isResetPasswordPending
                ? isWelcome
                  ? 'Setting...'
                  : 'Resetting...'
                : isWelcome
                  ? 'Set password'
                  : 'Reset Password'}
            </Button>

            <div className="text-center text-sm">
              <Link
                to="/login"
                className="text-muted-foreground underline-offset-4 hover:text-primary hover:underline"
              >
                Back to Sign In
              </Link>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
}
